import { z } from "zod";
import { ColumnSchema, ViewSchema, ViewType } from "./views";

export enum ServiceTemplateSchedulesViewColumn {
    TEMPLATE_NAME = "templateName",
    CLIENT = "client",
    ENTITY = "entity",
    SCHEDULE = "schedule",
    PERIOD = "period",
    STATUS = "status",
    // Merged "Next service start"/"Next service end" (formerly two separate
    // columns, START_DATE/END_DATE) into a single period column, formatted
    // with formatExactDateRangePeriod. Sorting still reuses the existing
    // START_DATE_ASC/DESC sort values (chronological by service start date;
    // see the column config's `sort`).
    NEXT_SERVICE_PERIOD = "nextServicePeriod",
    NEXT_APPLICATION_DATE = "nextApplicationDate",
    NEXT_WORK_START = "nextWorkStart",
    NEXT_WORK_END = "nextWorkEnd",
    ENGAGEMENT_TEAM = "engagementTeam",
    ESTIMATED_TIME = "estimatedTime",
    CREATED_AT = "createdAt",
    ACTIONS = "actions",
}

export enum ServiceTemplateSchedulesSortValue {
    TEMPLATE_NAME_ASC = "TEMPLATE_NAME_ASC",
    TEMPLATE_NAME_DESC = "TEMPLATE_NAME_DESC",
    CLIENT_ASC = "CLIENT_ASC",
    CLIENT_DESC = "CLIENT_DESC",
    ENTITY_ASC = "ENTITY_ASC",
    ENTITY_DESC = "ENTITY_DESC",
    STATUS_ASC = "STATUS_ASC",
    STATUS_DESC = "STATUS_DESC",
    PERIOD_ASC = "PERIOD_ASC",
    PERIOD_DESC = "PERIOD_DESC",
    // Sort by the next occurrence's serviceStartDate/serviceEndDate — see the
    // NEXT_SERVICE_PERIOD column id comment above.
    START_DATE_ASC = "START_DATE_ASC",
    START_DATE_DESC = "START_DATE_DESC",
    END_DATE_ASC = "END_DATE_ASC",
    END_DATE_DESC = "END_DATE_DESC",
    NEXT_APPLICATION_DATE_ASC = "NEXT_APPLICATION_DATE_ASC",
    NEXT_APPLICATION_DATE_DESC = "NEXT_APPLICATION_DATE_DESC",
    NEXT_WORK_START_ASC = "NEXT_WORK_START_ASC",
    NEXT_WORK_START_DESC = "NEXT_WORK_START_DESC",
    NEXT_WORK_END_ASC = "NEXT_WORK_END_ASC",
    NEXT_WORK_END_DESC = "NEXT_WORK_END_DESC",
    ESTIMATED_TIME_ASC = "ESTIMATED_TIME_ASC",
    ESTIMATED_TIME_DESC = "ESTIMATED_TIME_DESC",
    CREATED_AT_ASC = "CREATED_AT_ASC",
    CREATED_AT_DESC = "CREATED_AT_DESC",
}

const makeColumnSchema = (id: ServiceTemplateSchedulesViewColumn) =>
    ColumnSchema.extend({ id: z.literal(id) });

// Legacy column ids that were merged into NEXT_SERVICE_PERIOD (formerly the two
// separate START_DATE/END_DATE columns). They no longer exist in the view
// config, but persisted view caches may still contain them. They stay in the
// parse schema — not in the enum or config — so a stale cache validates instead
// of being discarded wholesale by useWorkspaceUserCache; mergeCachedView then
// drops these unknown ids per-column, preserving the rest of the user's
// customization (order, visibility, sizing, pins).
const makeLegacyColumnSchema = <T extends string>(id: T) =>
    ColumnSchema.extend({ id: z.literal(id) });

export const ServiceTemplateSchedulesViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.SERVICE_TEMPLATE_SCHEDULES),
    columns: z.array(
        z.discriminatedUnion("id", [
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.TEMPLATE_NAME),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.CLIENT),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.ENTITY),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.SCHEDULE),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.PERIOD),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.STATUS),
            makeColumnSchema(
                ServiceTemplateSchedulesViewColumn.NEXT_SERVICE_PERIOD,
            ),
            makeColumnSchema(
                ServiceTemplateSchedulesViewColumn.NEXT_APPLICATION_DATE,
            ),
            makeColumnSchema(
                ServiceTemplateSchedulesViewColumn.NEXT_WORK_START,
            ),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.NEXT_WORK_END),
            makeColumnSchema(
                ServiceTemplateSchedulesViewColumn.ENGAGEMENT_TEAM,
            ),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.ESTIMATED_TIME),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.CREATED_AT),
            makeColumnSchema(ServiceTemplateSchedulesViewColumn.ACTIONS),
            makeLegacyColumnSchema("startDate"),
            makeLegacyColumnSchema("endDate"),
        ]),
    ),
});

export type ServiceTemplateSchedulesView = z.infer<
    typeof ServiceTemplateSchedulesViewSchema
>;
